<!--
  The panel markup (header, new-chat, session list) is shared between the two mountings below and
  lives in one place: a drawer overlay (Floating/Full screen views) or a splitter pane docked next
  to the chat (Sidebar view). Only the wrapper changes; the structure itself doesn't.
-->
<ng-template #panelContent>
    <div class="sidebar-panel">
        <div class="sidebar-header">
            <button kendoButton fillMode="flat" [svgIcon]="closeIcon" [attr.aria-label]="'chatbot.closeSidebar' | translate" (click)="close()"></button>
        </div>

        <button kendoButton fillMode="flat" [svgIcon]="newChatIcon" class="new-chat" (click)="onNewChat()">
            {{ "chatbot.newChat" | translate }}
        </button>

        <div class="section-title">{{ "chatbot.chats" | translate }}</div>

        @if (sessions().length === 0) {
            <p class="empty-history">{{ "chatbot.noConversations" | translate }}</p>
        }

        <ul class="session-list">
            @for (session of sessions(); track session.id) {
                <li class="session-row" [class.active]="session.clientToken === activeClientToken()">
                    @if (renamingId() === session.id) {
                        <!--
                            Inline rather than in a dialog: the row already shows the title, and editing it
                            in place keeps the list as the single reference for what is being renamed.
                            Committing on blur as well as on Enter is what makes clicking away feel right.
                        -->
                        <input
                            #renameInput
                            kendoTextBox
                            class="rename-input"
                            [ngModel]="renameDraft()"
                            (ngModelChange)="renameDraft.set($event)"
                            (keydown.enter)="commitRename(session)"
                            (keydown.escape)="cancelRename()"
                            (blur)="commitRename(session)"
                            [attr.aria-label]="'chatbot.rename' | translate"
                        />
                    } @else {
                        <!--
                            A real `<button>`, not a clickable `<li>`: it is what makes the row reachable by
                            keyboard and announced as an action, without a hand-rolled `role`/`tabindex`
                            pair. The three-dots button is its sibling, since a button cannot nest inside one.
                        -->
                        <button type="button" class="session-text" (click)="onSelect(session)">
                            <span class="session-title" [title]="session.title">{{ session.title }}</span>
                            <span class="session-subtitle">
                                {{ formatTimestamp(session.firstMessageAt) }}
                                @if (session.isTest) {
                                    <span class="test-badge">{{ "chatbot.testConversation" | translate }}</span>
                                }
                            </span>
                        </button>

                        @if (allowSessionManagement()) {
                            <!--
                                Three **vertical** dots, on explicit request. The Rovo mockup this layout comes
                                from uses horizontal ones. Always rendered, not revealed on hover: on a touch
                                screen there is no hover to reveal it with.
                            -->
                            <button
                                kendoButton
                                fillMode="flat"
                                size="small"
                                class="row-menu-button"
                                [svgIcon]="menuIcon"
                                [attr.aria-label]="'chatbot.conversationActions' | translate"
                                (click)="openRowMenu($event, session)"
                            ></button>
                        }
                    }
                </li>
            }
        </ul>
    </div>
</ng-template>

<!--
  The chat is projected as the drawer's content so the sidebar owns the whole container. It
  always floats above the conversation with Kendo's own scrim, whichever of the chatbot's three
  views (Floating/Sidebar/Full screen) is active. See the `expanded` doc comment above for why
  this must not vary by view.
-->
<kendo-drawer-container class="sidebar-container">
    <kendo-drawer [expanded]="expanded()" mode="overlay" position="start" [width]="320" [autoCollapse]="false" (expandedChange)="expanded.set($event)">
        <ng-template kendoDrawerTemplate>
            <ng-container [ngTemplateOutlet]="panelContent"></ng-container>
        </ng-template>
    </kendo-drawer>

    <kendo-drawer-content class="sidebar-content">
        <ng-container [ngTemplateOutlet]="chatContent"></ng-container>
    </kendo-drawer-content>
</kendo-drawer-container>

<!--
  The single place `<ng-content>` appears: Angular assigns unclaimed projected content to the
  last unselected `<ng-content>` in the template, so it can only exist once here and be reused via
  `ngTemplateOutlet`. Repeating the tag itself across the branches above would silently break
  projection into the others.
-->
<ng-template #chatContent>
    <ng-content></ng-content>
</ng-template>

<!--
  One instance for the whole list, shown at the offset of whichever row's button was clicked
  (`openRowMenu`). `showOn="none"` leaves opening entirely to that call, so the menu never appears
  on a stray right-click over the sidebar.
-->
<!-- `!`: Kendo's own `appendTo` input type is non-optional even though it tolerates `undefined`
     at runtime, falling back to its own default root container. See `appendTo`'s doc comment.
-->
<kendo-contextmenu #rowMenu [items]="menuItems()" [appendTo]="appendTo()!" showOn="none" (select)="onMenuSelect($event)"></kendo-contextmenu>

@if (pendingDelete(); as session) {
    <kendo-dialog [title]="'chatbot.deleteConversation' | translate" (close)="cancelDelete()" [minWidth]="280" [width]="420">
        <p class="delete-prompt">{{ "chatbot.deleteConversationConfirm" | translate: { title: session.title } }}</p>
        <kendo-dialog-actions>
            <button kendoButton (click)="cancelDelete()">{{ "chatbot.cancel" | translate }}</button>
            <button kendoButton themeColor="error" (click)="confirmDelete()">{{ "chatbot.delete" | translate }}</button>
        </kendo-dialog-actions>
    </kendo-dialog>
}
